import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import {
  type PaymentCard,
  type PlanPeriod,
  useGetIsTrialActiveQuery,
} from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { Box } from '@frameio/vapor';
import StripeAddressElement from '../../../../billing/components/StripeAddressElement';
import StripeCardElements from '../../../../billing/components/StripeCardElements';
import StripeElementContainer from '../../../../billing/components/StripeElementContainer';
import { StripeFormError } from '../../../../billing/components/StripeFormError';
import StripeTaxIdElement from '../../../../billing/components/StripeTaxIdElement';
import useStripeTaxIdField from '../../../../billing/hooks/useStripeTaxIdField';
import useSubmitStripeForm from '../../../../billing/hooks/useSubmitStripeForm';
import { trackingBillingFields } from '../../../../billing/hooks/useUpdatePaymentCard';
import type { SubscriptionUpdateSource } from '../../../../feature-gate/config';
import { useUpdateTaxId } from '../hooks';
import { getIsBillingCountryVatExemptEligible } from '../utils';
import type { PAYMENT_STEPS } from './constants';
import styles from './PlanPaymentSubmit.module.css';

type PlanPaymentSubmitProps = {
  onCancel: () => void;
  onComplete: () => void;
  paymentMethod?: PaymentCard;
  period: PlanPeriod;
  setStep?: Dispatch<SetStateAction<PAYMENT_STEPS>>;
  subscriptionUpdateSource?: SubscriptionUpdateSource;
};

export const PlanPaymentSubmit = ({
  onCancel,
  onComplete,
  paymentMethod,
  period,
  subscriptionUpdateSource,
}: PlanPaymentSubmitProps) => {
  const [isCardFormComplete, setIsCardFormComplete] = useState(false);
  const [isAddressFormComplete, setIsAddressFormComplete] = useState(false);
  const [addressError, setAddressError] = useState<string | undefined>();

  const isCheckoutVatIdCollectionEnabled = useFeatureFlagEnabled(
    'v4.checkout_vat_id_collection'
  );

  const accountId = useActiveAccountId() ?? '';

  const { data: isTrialActiveData, loading: loadingTrialActiveData } =
    useGetIsTrialActiveQuery({
      variables: {
        accountId,
      },
      skip: !accountId,
    });

  const isTrialActive =
    isTrialActiveData?.account?.subscription.trial.isActive ?? false;
  const isCardSet = !!paymentMethod?.card;

  const savedTaxId = paymentMethod?.taxId;
  const savedTaxType = paymentMethod?.taxType;

  const [usersCountry, setUsersCountry] = useState('');
  const isBillingCountryVatExemptEligible =
    getIsBillingCountryVatExemptEligible(usersCountry);

  const showTaxIdCollectionInput =
    isCheckoutVatIdCollectionEnabled && isBillingCountryVatExemptEligible;

  const {
    taxIdValue,
    defaultTaxId,
    defaultTaxIdType,
    isBlocking: isTaxIdBlocking,
    onChange: handleTaxIdChange,
    reset: resetTaxId,
  } = useStripeTaxIdField({
    taxId: savedTaxId,
    taxType: savedTaxType,
    enabled: showTaxIdCollectionInput,
  });

  useEffect(() => {
    resetTaxId();
  }, [resetTaxId]);

  const { updateTaxId } = useUpdateTaxId(
    taxIdValue?.taxId ?? '',
    taxIdValue?.taxType ?? ''
  );

  const [handleSubmit, { apolloError, isLoading, reset, stripeError }] =
    useSubmitStripeForm({
      includeCard: true,
      onCompleted: onComplete,
      onSubmit: updateTaxId,
      paymentCard: paymentMethod,
      shouldSetAddressInReactiveState: isTrialActive && !isCardSet,
      subscriptionUpdateSource,
      trackingBillingFieldsUpdated:
        trackingBillingFields.paymentMethodAndBillingAddress,
    });

  const hasError = !!apolloError || !!addressError || !!stripeError;

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [onCancel, reset]);

  const paymentMethodCard = paymentMethod?.card;

  const isSubmitButtonDisabled =
    isLoading ||
    loadingTrialActiveData ||
    !isCardFormComplete ||
    (!paymentMethodCard && !isAddressFormComplete) ||
    isTaxIdBlocking;

  return (
    <div className={styles.container}>
      <StripeElementContainer
        hasPaymentMethod={!!paymentMethod}
        isCollapsed={false}
        isSubmitDisabled={!!addressError || isSubmitButtonDisabled}
        isVisible={true}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      >
        <StripeCardElements
          isEditing={true}
          onCardFormCompleteChange={setIsCardFormComplete}
        />
        {paymentMethod && (
          <div className={styles.addressContainer}>
            <div className={styles.addressElementContainer}>
              <StripeAddressElement
                onElementCompleteChange={setIsAddressFormComplete}
                onError={setAddressError}
                paymentCard={paymentMethod}
                period={period}
                setUsersCountry={setUsersCountry}
              />
              {hasError && (
                <StripeFormError
                  apolloErrorMessage={apolloError?.message}
                  messageForDisplay={addressError || stripeError?.message}
                />
              )}
            </div>
          </div>
        )}

        {showTaxIdCollectionInput && (
          <Box paddingTop={16}>
            <StripeTaxIdElement
              defaultTaxId={defaultTaxId}
              defaultTaxIdType={defaultTaxIdType}
              onChange={handleTaxIdChange}
            />
          </Box>
        )}
      </StripeElementContainer>
    </div>
  );
};
